/**
 * `<ScoreRing>` — circular percent indicator (conic gradient + center text).
 *
 * Foundations spec: design/design-system-foundations.md §9.17.
 * Outer ring is a conic-gradient from `0` to `score%` in the score-tier
 * color, then `surface-subtle` from `score%` to `100%`. Inner disc
 * (inset = thickness) shows the numeric score in the same tier color
 * via tabular-nums for digit-stable rendering.
 *
 * 30f Phase B6 — additive only. Candidate page sidebar (which consumes
 * this) lands in 30j.
 */
import type { JSX } from "react";
import { fitColorHex, fitScoreColor } from "@/lib/fitScore";

interface ScoreRingProps {
  /** 0..100 (or null for "—" empty state). */
  score: number | null;
  /** Outer diameter in px. Default 70 per spec. */
  size?: number;
  /** Ring thickness in px. Default 7 per spec. */
  thickness?: number;
}

export function ScoreRing({
  score,
  size = 70,
  thickness = 7,
}: ScoreRingProps): JSX.Element {
  const isEmpty = score === null || Number.isNaN(score);
  const clamped = isEmpty
    ? 0
    : Math.max(0, Math.min(100, Math.round(score as number)));
  const tier = isEmpty ? null : fitScoreColor(clamped);
  const color = tier ? fitColorHex(tier) : "var(--text-tertiary)";

  // Outer ring fill: conic gradient up to `clamped%`; rest = surface-subtle.
  const ringStyle: React.CSSProperties = {
    width: size,
    height: size,
    backgroundImage: isEmpty
      ? "none"
      : `conic-gradient(${color} ${clamped}%, var(--surface-subtle) ${clamped}%)`,
    backgroundColor: isEmpty ? "var(--surface-subtle)" : undefined,
  };

  return (
    <span
      className="relative inline-flex items-center justify-center rounded-full"
      style={ringStyle}
      role="img"
      aria-label={isEmpty ? "no score" : `score ${clamped}%`}
    >
      <span
        className="absolute flex items-center justify-center rounded-full bg-surface-elevated text-base font-bold tabular-nums"
        style={{
          inset: thickness,
          color,
        }}
      >
        {isEmpty ? "—" : `${clamped}%`}
      </span>
    </span>
  );
}
